import type { ActionTree } from 'vuex'
import dayjs from 'dayjs'
import type { RecipientState } from './types'
import type { ExportMessageRecipientsParams, FetchMessageRecipientsParams } from '@/api/recipients/types'
import type { RootState } from '@/store/modules/types'
import { getContext } from '@/utils/general'
import { magicDownload } from '@/utils/html-element/'

type FetchRecipientsPayload = Omit<FetchMessageRecipientsParams, 'skip'> & { reload?: boolean }

// Controller of the request currently allowed to write in the store. Kept out of the
// (reactive) state on purpose: a response whose controller is not this one is stale
// (superseded by a reload or discarded by a reset) and must never reach the state.
let currentController: AbortController | null = null

export const recipientActions: ActionTree<RecipientState, RootState> = {
  async FETCH_MESSAGE_RECIPIENTS(
    { rootState, state, commit },
    { reload = false, ...params }: FetchRecipientsPayload,
  ) {
    if (!rootState.authModule.account) { return null }
    // "Load more" while a page is already loading would request the same page twice.
    // A reload always wins: it cancels whatever is in flight.
    if (!reload && state.isFetchingRecipients) { return null }
    const { $api } = getContext()

    currentController?.abort()
    const controller = new AbortController()
    currentController = controller

    commit('START_RECIPIENTS_FETCH', reload)
    try {
      const recipients = await $api.recipients.fetchMessageRecipients(
        { ...params, skip: reload ? 0 : state.recipientsOffset },
        controller.signal,
      )
      if (controller !== currentController) { return null }
      if (!recipients) {
        commit('RECIPIENTS_FETCH_FAILED')
        return false
      }
      commit('RECIPIENTS_FETCHED', { recipients, reload, top: params.top })
      return true
    }
    catch (error) {
      if (controller !== currentController) { return null }
      console.error(error)
      // The error notification is already pushed by requestApi
      commit('RECIPIENTS_FETCH_FAILED')
      return false
    }
  },

  /** Cancels the request in flight (if any) and empties the recipients. */
  RESET_MESSAGE_RECIPIENTS({ commit }) {
    currentController?.abort()
    currentController = null
    commit('RESET_RECIPIENTS')
  },

  async FETCH_CLICKED_URL_RECIPIENTS_COUNT(
    { rootState },
    params: { messageTaskOid: number, promoterMessageOid?: number | null, clickedUrl: string, searchString?: string | null, isSms?: boolean },
  ) {
    if (!rootState.authModule.account) { return null }
    const { promoterOid } = rootState.authModule.account
    const { $api, $arNotification } = getContext()

    try {
      return await $api.recipients.fetchClickedUrlRecipientCount(promoterOid, params)
    }
    catch (error) {
      console.error(error)
      $arNotification.push({ type: 'error', message: 'Failed to fetch message recipient count' })
      return null
    }
  },

  async FETCH_SUPPRESSED_RECIPIENTS_COUNT({ rootState }, { messageTaskOid }: { messageTaskOid: number }) {
    if (!rootState.authModule.account) { return 0 }
    const { promoterOid } = rootState.authModule.account
    const { $api } = getContext()

    try {
      return await $api.recipients.fetchSuppressedRecipientsCount(promoterOid, messageTaskOid)
    }
    catch (error) {
      console.error('Failed to fetch suppressed recipients count:', error)
      return 0
    }
  },

  async FETCH_REJECTED_RECIPIENTS_COUNT({ rootState }, { messageTaskOid }: { messageTaskOid: number }) {
    if (!rootState.authModule.account) { return null }
    const { $api } = getContext()

    try {
      return await $api.recipients.fetchRejectedRecipientsCount(messageTaskOid)
    }
    catch (error) {
      console.error('Failed to fetch rejected recipients count:', error)
      return null
    }
  },

  async EXPORT_MESSAGE_RECIPIENTS_CSV({ rootState, commit }, params: ExportMessageRecipientsParams) {
    if (!rootState.authModule.account) { return null }
    const { promoterOid } = rootState.authModule.account
    const { $api, $arNotification } = getContext()

    commit('SET_IS_EXPORTING_RECIPIENTS', true)
    try {
      const { data, status } = await $api.recipients.exportMessageRecipientsCsv(promoterOid, params)
      if (status === 200) {
        magicDownload(data, `recipients-${dayjs().format('DD-MM-YYYY')}.csv`)
      }
      else {
        $arNotification.push({ type: 'warning', message: data, timeout: 5000 })
      }
      return true
    }
    catch (error) {
      console.error(error)
      $arNotification.push({ type: 'error', message: 'Failed to export message recipients' })
      return false
    }
    finally {
      commit('SET_IS_EXPORTING_RECIPIENTS', false)
    }
  },
}
